/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { Col, Modal, Row, Spin } from "antd";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useEffectOnce } from "react-use";
import Input from "../../components/Input/Input";
import Select from "../../components/Select/Select";
import TablePagination from "../../components/Table/TablePagination";
import Text from "../../components/Text/Text";
import { getNewsShop } from "../../datasource/News";
import { getShopGroup } from "../../datasource/ShopGroupDatasoure";
import { zoneDatasource } from "../../datasource/ZoneDatasource";
import color from "../../resource/color";
import { numberFormatter } from "../../utility/Formatter";
import {
  NewsShopEmpty,
  newsShopKey,
  parseNewsShopResponse,
  uniqueNewsShops,
} from "./CreateNewsStep/NewsStepTwoShop";

const PAGE_SIZE = 10;
const SCROLL_OFFSET = 40;
const LOAD_MORE_DELAY = 500;
const MODAL_HEIGHT = 740;
const STATUS_BAR_HEIGHT = 46;
const TABLE_SCROLL_Y = 485;

const ModalStyled = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    /* padding 0 เพื่อให้เส้นคั่นใต้หัว modal ลากเต็มความกว้าง แล้วไปใส่ padding ที่ header/body แทน */
    padding: 0;
    height: ${MODAL_HEIGHT}px;
    overflow: hidden;
  }
  .ant-modal-header {
    margin: 0;
    padding: 16px 24px;
    border-bottom: 1px solid ${color.border};
    border-radius: 8px 8px 0 0;
  }
  .ant-modal-body {
    padding: 0 24px 24px;
  }
  .ant-modal-close {
    top: 16px;
    inset-inline-end: 16px;
    width: 32px;
    height: 32px;
  }
`;

/* ครอบตารางเพื่อปรับสไตล์เฉพาะจุดที่ต่างจาก TablePagination (ไม่มีกรอบนอก มีเส้นคั่นหัวคอลัมน์) */
const TableWrap = styled.div`
  .ant-table-thead > tr > th {
    padding: 12px 24px !important;
  }
  /* TablePagination ซ่อนเส้นคั่นหัวคอลัมน์ไว้ ดีไซน์นี้ต้องการเส้น จึงเปิดกลับด้วย selector เดียวกัน */
  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
      [colspan]
    )::before {
    display: block !important;
    width: 1px !important;
    height: 28px !important;
    background-color: ${color.border} !important;
  }
  .ant-table-body {
    overflow-y: auto !important;
  }
  .ant-table-body::-webkit-scrollbar {
    width: 6px;
  }
  .ant-table-body::-webkit-scrollbar-track {
    background: transparent;
  }
  .ant-table-body::-webkit-scrollbar-thumb {
    background: ${color.Text3};
    border-radius: 8px;
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  height: ${STATUS_BAR_HEIGHT}px;
`;

interface Props {
  newsId: string;
  countShop?: number;
  visible: boolean;
  onCancel: () => void;
}

interface NewsShopItem {
  customerCompanyId?: string;
  customerNo?: string;
  customerName?: string;
  zone?: string;
  customerGroupName?: string;
  [key: string]: any;
}

export const ModalNewsShop: React.FC<Props> = ({ newsId, countShop = 0, visible, onCancel }) => {
  const [zoneList, setZoneList] = useState<any[]>([]);
  const [groupList, setGroupList] = useState<any[]>([]);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [zone, setZone] = useState<string>("");
  const [customerGroupName, setCustomerGroupName] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>();
  const [sortingDirection, setSortingDirection] = useState<"ASC" | "DESC">();

  const [shopList, setShopList] = useState<NewsShopItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(countShop);
  const [totalAll, setTotalAll] = useState<number>(countShop);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const fetchingRef = useRef<boolean>(false);
  const loadMoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFilter = !!searchText || !!zone || !!customerGroupName;

  const clearLoadMoreTimer = () => {
    if (loadMoreTimerRef.current) {
      clearTimeout(loadMoreTimerRef.current);
      loadMoreTimerRef.current = null;
    }
  };

  useEffect(() => clearLoadMoreTimer, []);

  useEffectOnce(() => {
    fetchZone();
    fetchShopGroup();
  });

  const fetchZone = async () => {
    await zoneDatasource
      .getAllZoneByCompany(JSON.parse(localStorage.getItem("profile")!)?.company)
      .then((res) => setZoneList(res || []))
      .catch((err) => console.log(err));
  };

  const fetchShopGroup = async () => {
    await getShopGroup({
      page: 1,
      take: 999,
      company: JSON.parse(localStorage.getItem("profile")!)?.company,
      isActive: true,
    })
      .then((res) => setGroupList(res?.responseData?.data || []))
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput.trim()), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    clearLoadMoreTimer();
    if (!visible) {
      fetchingRef.current = false;
      setLoadingMore(false);
      return;
    }
    fetchShopList(1, false);
  }, [visible, searchText, zone, customerGroupName, sortBy, sortingDirection]);

  const fetchShopList = async (nextPage: number, append: boolean) => {
    try {
      fetchingRef.current = true;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const res = await getNewsShop({
        newsId,
        page: nextPage,
        take: PAGE_SIZE,
        searchText: searchText || undefined,
        zone: zone || undefined,
        customerGroupName: customerGroupName || undefined,
        sortBy,
        sortingDirection,
      });
      const { data, count: total } = parseNewsShopResponse(res);

      const merged = uniqueNewsShops(append ? [...shopList, ...data] : data);
      const gained = merged.length - (append ? shopList.length : 0);
      setShopList(merged);
      setPage(nextPage);
      setCount(total);
      if (!hasFilter && total) setTotalAll(total);
      setHasMore(gained > 0 && data.length >= PAGE_SIZE && (total ? merged.length < total : true));
    } catch (e) {
      console.log(e);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleTableScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_OFFSET;
    if (!reachedBottom) return;
    if (fetchingRef.current || loading || loadingMore || !hasMore) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    const nextPage = page + 1;
    clearLoadMoreTimer();
    loadMoreTimerRef.current = setTimeout(() => {
      loadMoreTimerRef.current = null;
      fetchShopList(nextPage, true);
    }, LOAD_MORE_DELAY);
  };

  const onTableChange = (_pagination: any, _filters: any, sorter: any) => {
    const field = Array.isArray(sorter) ? sorter[0]?.field : sorter?.field;
    const order = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
    setSortBy(order ? `${field}` : undefined);
    setSortingDirection(order ? (order === "ascend" ? "ASC" : "DESC") : undefined);
  };

  const columns = [
    {
      title: "รหัสร้านค้า",
      dataIndex: "customerNo",
      key: "customerNo",
      width: "16%",
      render: (value: any, row: NewsShopItem) => (
        <Text fontFamily='Sarabun' level={5}>
          {value || "-"}
        </Text>
      ),
    },
    {
      title: "ชื่อร้านค้า",
      dataIndex: "customerName",
      key: "customerName",
      width: "53%",
      sorter: true,
      showSorterTooltip: false,
      render: (value: any, row: NewsShopItem) => (
        <Text fontFamily='Sarabun' level={5}>
          {value || "-"}
        </Text>
      ),
    },
    {
      title: "เขต",
      dataIndex: "zone",
      key: "zone",
      width: "15%",
      sorter: true,
      showSorterTooltip: false,
      render: (value: any, row: NewsShopItem) => (
        <Text fontFamily='Sarabun' level={5}>
          {value || "-"}
        </Text>
      ),
    },
    {
      title: "กลุ่มลูกค้า",
      dataIndex: "customerGroupName",
      key: "customerGroupName",
      width: "16%",
      sorter: true,
      showSorterTooltip: false,
      render: (value: any, row: NewsShopItem) => (
        <Text fontFamily='Sarabun' level={5}>
          {value || "-"}
        </Text>
      ),
    },
  ];

  return (
    <ModalStyled
      open={visible}
      onCancel={onCancel}
      width={1050}
      footer={false}
      centered
      title={
        <Text fontSize={20} fontWeight={700}>
          {`ร้านค้าที่เห็นข่าว ${numberFormatter(totalAll || 0, 0)} ร้าน`}
        </Text>
      }
    >
      <Row gutter={12} align='middle' style={{ padding: "16px 0" }}>
        <Col flex='auto' style={{ top: 3 }}>
          <Input
            value={searchInput}
            placeholder='ค้นหารหัส / ชื่อร้านค้า'
            suffix={<SearchOutlined style={{ color: color.Grey }} />}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Select
            value={zone || undefined}
            placeholder='เขตทั้งหมด'
            style={{ width: "100%" }}
            allowClear={!!zone}
            data={[
              ...zoneList.map((item) => ({
                label: item.zoneName,
                key: item.zoneName,
                value: item.zoneName,
              })),
            ]}
            onChange={(value) => setZone(value || "")}
          />
        </Col>
        <Col span={4}>
          <Select
            value={customerGroupName || undefined}
            placeholder='กลุ่มลูกค้าทั้งหมด'
            style={{ width: "100%" }}
            allowClear={!!customerGroupName}
            data={[
              ...groupList.map((item) => ({
                label: item.customerGroupName,
                key: item.customerGroupName,
                value: item.customerGroupName,
              })),
            ]}
            onChange={(value) => setCustomerGroupName(value || "")}
          />
        </Col>
      </Row>
      <TableWrap>
        <TablePagination
          columns={columns as any}
          data={shopList.map((row, index) => ({ ...row, key: `${newsShopKey(row)}-${index}` }))}
          keyId='key'
          isLoading={loading}
          scroll={{ y: TABLE_SCROLL_Y }}
          onScroll={handleTableScroll}
          onChange={onTableChange}
          locale={{
            emptyText: (
              <NewsShopEmpty text={hasFilter ? "ไม่พบร้านค้า" : "ไม่มีร้านค้าที่เห็นข่าว"} />
            ),
          }}
        />
      </TableWrap>
      <StatusBar>
        {loadingMore && (
          <Row justify='center' align='middle' style={{ gap: 8, width: "100%" }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
            <Text level={6} color='Text3'>
              กำลังโหลดข้อมูล...
            </Text>
          </Row>
        )}
      </StatusBar>
    </ModalStyled>
  );
};

export default ModalNewsShop;
